import { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings } from 'lucide-react';
import type { PeerData } from '../types';

interface RadarViewProps {
  peerCount: number;
  isConnected: boolean;
  remotePeer: PeerData | null;
  onPeerClick: (peer: PeerData) => void;
  connectionState: 'idle' | 'waiting' | 'connecting' | 'connected';
  spaceUrl?: string;
  // Local discovery
  isLocalDiscoveryEnabled?: boolean;
  localPeers?: PeerData[];
  onOpenSettings?: () => void;
}

export function RadarView({ 
  peerCount, 
  isConnected, 
  remotePeer,
  onPeerClick,
  connectionState,
  spaceUrl,
  isLocalDiscoveryEnabled = false,
  localPeers = [],
  onOpenSettings
}: RadarViewProps) {
  const [linkCopied, setLinkCopied] = useState(false);
  // Radar pulse animation
  const pulseVariants = {
    initial: { scale: 1, opacity: 0.5 },
    animate: { 
      scale: [1, 1.5, 2],
      opacity: [0.5, 0.2, 0],
    }
  };

  // Scanning line animation
  const scanVariants = {
    animate: {
      rotate: 360,
    }
  };

  // Peer node animation
  const peerVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
    },
    connected: {
      scale: 1,
    }
  };

  const handleCopyLink = async () => {
    if (!spaceUrl) return;
    
    try {
      await navigator.clipboard.writeText(spaceUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const getStatusDisplay = () => {
    switch (connectionState) {
      case 'waiting':
        return {
          icon: '📡',
          title: 'Scanning for Peers',
          subtitle: 'Share your link to connect',
          color: 'text-blue-400'
        };
      case 'connecting':
        return {
          icon: '🔗',
          title: 'Establishing Connection',
          subtitle: 'Securing peer-to-peer tunnel',
          color: 'text-yellow-400'
        };
      case 'connected':
        return {
          icon: '✓',
          title: `Connected to ${remotePeer?.name || 'Peer'}`,
          subtitle: 'Ready to transfer files',
          color: 'text-green-400'
        };
      default:
        return {
          icon: '🌐',
          title: 'Ready',
          subtitle: 'Waiting to scan',
          color: 'text-gray-400'
        };
    }
  };

  const status = getStatusDisplay();

  return (
    <div className="relative w-full h-[600px] flex flex-col items-center">
      {/* Settings Gear Icon */}
      {onOpenSettings && (
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={onOpenSettings}
            className="relative p-3 rounded-full bg-gray-800/50 hover:bg-gray-700/50 border-2 border-gray-600/50 hover:border-gray-500/50 text-gray-400 hover:text-white transition-all"
            title="Settings"
            aria-label="Open settings"
          >
            <Settings size={20} />
            {/* Local discovery indicator badge */}
            {isLocalDiscoveryEnabled && localPeers.length > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-purple-500 text-white text-xs rounded-full border-2 border-gray-900">
                {localPeers.length}
              </span>
            )}
          </button>
        </div>
      )}

      <div className="relative w-full flex-1 flex items-center justify-center">
      {/* Radar background circles */}
      <div className="absolute inset-0 flex items-center justify-center">
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full border border-white/10"
            style={{
              width: `${(i + 1) * 25}%`,
              height: `${(i + 1) * 25}%`,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ delay: i * 0.1 }}
          />
        ))}
      </div>

      {/* Radar scanning line (only when waiting or connecting) */}
      {(connectionState === 'waiting' || connectionState === 'connecting') && (
        <motion.div
          className="absolute w-full h-full flex items-center justify-center pointer-events-none"
          variants={scanVariants}
          animate="animate"
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        >
          <div className="absolute w-1/2 h-0.5 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent origin-left" />
        </motion.div>
      )}

      {/* Pulse animation from center (when waiting) */}
      {connectionState === 'waiting' && (
        <>
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full border-2 border-blue-500/30"
              style={{ width: '100px', height: '100px' }}
              variants={pulseVariants}
              initial="initial"
              animate="animate"
              transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: i * 0.7 }}
            />
          ))}
        </>
      )}

      {/* Center node (current user) */}
      <motion.div 
        className="relative z-10"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <div className="relative">
          {/* Glow effect when connected */}
          {isConnected && (
            <motion.div
              className="absolute inset-0 rounded-full bg-green-500/20 blur-xl"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
                type: "tween"
              }}
            />
          )}
          
          {/* Make center clickable when connected and we have a remote peer */}
          <button
            disabled={!isConnected || !remotePeer}
            onClick={() => remotePeer && onPeerClick(remotePeer)}
            className={`relative w-24 h-24 rounded-full flex items-center justify-center shadow-2xl border-4 transition ${
              isConnected && remotePeer
                ? 'bg-gradient-to-br from-green-500 to-emerald-600 border-green-400/50 hover:scale-105'
                : 'bg-gradient-to-br from-blue-500 to-purple-600 border-white/20 cursor-default'
            }`}
            title={isConnected && remotePeer ? 'Click to send a file' : undefined}
          >
            <span className="text-3xl">{status.icon}</span>
          </button>
          
          {/* Status text below center */}
          <div className="absolute top-full mt-6 left-1/2 -translate-x-1/2 text-center whitespace-nowrap">
            <div className={`text-lg font-semibold ${status.color}`}>
              {status.title}
            </div>
            {isConnected && remotePeer ? (
              <div className="text-sm text-gray-400 mt-1">
                Click the center to choose a file
              </div>
            ) : connectionState === 'waiting' && spaceUrl ? (
              <button
                onClick={handleCopyLink}
                className="text-sm text-blue-400 hover:text-blue-300 mt-1 transition-colors cursor-pointer"
              >
                {linkCopied ? '✓ Link copied!' : status.subtitle}
              </button>
            ) : (
              <div className="text-sm text-gray-400 mt-1">
                {status.subtitle}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Remote peer node */}
      {remotePeer && (
        <motion.div
          className="absolute top-1/2 right-[20%] -translate-y-1/2"
          variants={peerVariants}
          initial="hidden"
          animate={isConnected ? "connected" : "visible"}
          transition={{ type: "spring", stiffness: 260, damping: 20, duration: 0.5 }}
        >
          <button
            onClick={() => onPeerClick(remotePeer)}
            className="group relative"
          >
            {/* Connection line */}
            <svg 
              className="absolute top-1/2 left-1/2 pointer-events-none"
              style={{ 
                width: '200px', 
                height: '200px',
                transform: 'translate(-50%, -50%) rotate(-45deg)'
              }}
            >
              <motion.line
                x1="50%"
                y1="50%"
                x2="100%"
                y2="100%"
                stroke={isConnected ? '#10b981' : '#eab308'}
                strokeWidth="2"
                strokeDasharray="5,5"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5 }}
              />
            </svg>

            {/* Peer avatar */}
            <div className="relative">
              {isConnected && (
                <motion.div
                  className="absolute inset-0 rounded-full bg-green-500/20 blur-xl"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    type: "tween"
                  }}
                />
              )}
              
              <div className={`relative w-20 h-20 rounded-full flex items-center justify-center shadow-xl border-4 transition-all ${
                isConnected 
                  ? 'bg-gradient-to-br from-green-500 to-emerald-600 border-green-400/50 group-hover:scale-110' 
                  : 'bg-gradient-to-br from-yellow-500 to-orange-600 border-yellow-400/50 animate-pulse'
              }`}>
                <span className="text-2xl font-bold text-white">
                  {remotePeer.name.charAt(0).toUpperCase()}
                </span>
              </div>

              {/* Peer info tooltip */}
              <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-gray-900/90 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/10 whitespace-nowrap">
                  <div className="text-sm font-medium text-white">{remotePeer.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {isConnected ? 'Click to send file' : 'Connecting...'}
                  </div>
                </div>
              </div>
            </div>
          </button>
        </motion.div>
      )}

      {/* Local peers (when local discovery enabled) */}
      {isLocalDiscoveryEnabled && localPeers.map((peer, index) => {
        const angle = (index / localPeers.length) * 2 * Math.PI - Math.PI / 2;
        const radius = 280; // Increased from 200 to avoid overlap
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        
        return (
          <motion.div
            key={peer.id}
            className="absolute top-1/2 left-[70%]"
            style={{
              transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`
            }}
            variants={peerVariants}
            initial="hidden"
            animate="visible"
            transition={{ type: "spring", stiffness: 260, damping: 20, delay: index * 0.1 }}
          >
            <button
              onClick={() => onPeerClick(peer)}
              className="group relative"
            >
              {/* Peer avatar with purple theme for local peers */}
              <div className="relative">
                <div className="relative w-16 h-16 rounded-full flex items-center justify-center shadow-xl border-4 bg-gradient-to-br from-purple-500 to-indigo-600 border-purple-400/50 transition-all group-hover:scale-110">
                  <span className="text-xl font-bold text-white">
                    {peer.name.charAt(0).toUpperCase()}
                  </span>
                </div>

                {/* Peer info tooltip */}
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="bg-gray-900/90 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-purple-400/30 whitespace-nowrap">
                    <div className="text-xs font-medium text-white">{peer.name}</div>
                    <div className="text-xs text-purple-300 mt-0.5">
                      📶 Same Network
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </motion.div>
        );
      })}

      {/* Peer count indicator (when waiting) */}
      {peerCount > 0 && !isConnected && (
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 px-4 py-2 rounded-full">
            <span className="text-sm text-blue-300">
              {peerCount} {peerCount === 1 ? 'peer' : 'peers'} detected
            </span>
          </div>
        </motion.div>
      )}
      </div>
    </div>
  );
}
